import React, { useEffect, useState } from "react";
import {
  Button,
  createStyles,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";

import clsx from 'clsx';
import slash from 'slash';
import { dialog } from "./electron-modules";
import LocalScene from "./viewer/js/projections";
import { UnitConverter } from "./viewer/js/utilities";

import { Draggable } from 'react-beautiful-dnd';
import ListItem from '@material-ui/core/ListItem';
import {
  DragDropContext,
  Droppable,
  OnDragEndResponder
} from 'react-beautiful-dnd';

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fileButtonDiv: {
      paddingTop: theme.spacing(1),
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    error: {
      color: theme.palette.error.main
    },
    warning: {
      color: theme.palette.warning.main
    },
    draggingListItem: {
      background: 'rgb(235,235,235)'
    },
    staticListItem: {
      borderBottom: "2px dashed lightgray"
    },
    flexGrow: {
      flexGrow: 1
    }
  })
);

export const NumberField = (props) => {
  const {data, setData, min, max, adornment, ...other} = props;
  const step = props.step || 1.0;

  // NOTE: Space character is required for even positioning in UI
  const helperText = data.error ? `Accepted range is ${min} - ${max}` : " ";

  const endAdornment = adornment
    ? <InputAdornment position="end">{adornment}</InputAdornment>
    : null;

  const index = other.index ? other.index : 0;

  return (
    <TextField
      type="number"
      error={data.error}
      helperText={helperText}
      {...other}
      fullWidth
      InputProps={{
        inputProps: {max, min, step},
        endAdornment
      }}
      value={data.value}
      onChange={(event) => {
        let hasError = false;

        let floatValue = parseFloat(event.target.value);
        if (isNaN(floatValue)) {
          hasError = true;
        }

        if (floatValue > max) {
          hasError = true;
        } else if (floatValue < min) {
          hasError = true;
        }

        setData({
          value: event.target.value,
          error: hasError,
          index: index
        });
      }}
    />
  );
};

export const NumberFieldWithUnits = (props) => {
  const {data, min, max, setData, ...other} = props;
  const units = LocalScene.viewProjectionUnits;
  const step = props.step || 1.0;

  const [convertedMin, setConvertedMin] = useState(0);
  const [convertedMax, setConvertedMax] = useState(0);
  const [convertedData, setConvertedData] = useState({
    value: 0,
    error: false
  });

  const convertNearestStep = (value, roundFunc) => {
    if (units === "m") {
      return value;
    };

    const converted = converter.convert(value, "m", units, 2);
    let numSteps = roundFunc(converted / step);
    return parseFloat((numSteps * step).toFixed(2));
  };

  useEffect(() => {
    // Initial conversion from meters to new units
    setConvertedMin(convertNearestStep(min, Math.floor));
    setConvertedMax(convertNearestStep(max, Math.ceil));
    setConvertedData({
      value: convertNearestStep(data.value, Math.floor),
      error: data.error
    });
  }, []);

  useEffect(() => {
    // Update the original state
    setData({...convertedData});
  }, [convertedData]);

  return (
    <NumberField
      {...other}
      data={convertedData}
      setData={setConvertedData}
      min={convertedMin}
      max={convertedMax}
      adornment={units}
    />
  );
};

export const FolderWithLabel = (props) => {
  const {outputPath, setOutputPath} = props;
  const classes = useStyles();

  const handleFolderChange = () => {
    dialog.showOpenDialog({
      properties: ['openDirectory']
    }).then(result => {
      if (result.canceled) {
        return;
      }

      let folderPath = slash(result.filePaths[0]);
      setOutputPath(folderPath);
    });
  };

  return (
    <React.Fragment>
      <InputLabel>{props.title}</InputLabel>

      {!outputPath && (<Typography variant="caption">
        {props.label}
      </Typography>)}

      {outputPath && (<Typography variant="caption">
        {outputPath}
      </Typography>)}

      <div className={classes.fileButtonDiv}>
        {/* Select output folder */}
        <Button
          type="submit"
          onClick={handleFolderChange}
          className={clsx("", {[classes.error]: outputPath === null})}
        >
          Select Folder
        </Button>
      </div>
    </React.Fragment>
  );
};

export const FileWithLabel = (props) => {
  const {filePath, setFilePath} = props;
  const classes = useStyles();

  const handleFolderChange = () => {
    dialog.showOpenDialog({
      properties: ['openFile'],
      filters: props.filter
    }).then(result => {
      if (result.canceled) {
        return;
      }

      let folderPath = slash(result.filePaths[0]);
      setFilePath(folderPath);
    });
  };

  return (
    <React.Fragment>
      <InputLabel>{props.title}</InputLabel>

      {!filePath && (<Typography
        variant="caption"
        className={clsx("", {
          [classes.warning]: props.optional
        })}
      >
        {props.label}
      </Typography>)}

      {filePath && (<Typography variant="caption">
        {filePath}
      </Typography>)}

      <div className={classes.fileButtonDiv}>
        {/* Select file */}
        <Button
          type="submit"
          onClick={handleFolderChange}
          className={clsx("", {
            [classes.error]: (filePath === null) && (!props.optional),
          })}
        >
          Select File
        </Button>

        {/* Remove file (optional) */}
        {filePath && props.optional && (<Button
          type="submit"
          onClick={() => {
            setFilePath(null);
          }}
          color="secondary"
        >
          Remove File
        </Button>)}

      </div>
    </React.Fragment>
  );
};


export const FileWithLabelSave = (props) => {
  const {filePath, setFilePath, defaultPath, filter} = props;
  const classes = useStyles();

  const handleFolderChange = () => {
    dialog.showSaveDialog({
      defaultPath: defaultPath,
      filters: filter,
    }).then(result => {
      if (result.canceled) {
        return;
      }

      if (result.filePath) {
        const filePath = slash(result.filePath);
        setFilePath(filePath);
      }
    }).catch(error => {
      console.log(error);
    });
  };

  return (
    <React.Fragment>
      <InputLabel>{props.title}</InputLabel>

      {!filePath && (<Typography
        variant="caption"
        className={clsx("", {
          [classes.warning]: props.optional
        })}
      >
        {props.label}
      </Typography>)}

      {filePath && (<Typography variant="caption">
        {filePath}
      </Typography>)}

      <div className={classes.fileButtonDiv}>
        {/* Select file */}
        <Button
          type="submit"
          onClick={handleFolderChange}
          className={clsx("", {
            [classes.error]: (filePath === null) && (!props.optional),
          })}
        >
          Select File
        </Button>

        {/* Remove file (optional) */}
        {filePath && props.optional && (<Button
          type="submit"
          onClick={() => {
            setFilePath(null);
          }}
          color="secondary"
        >
          Remove File
        </Button>)}

      </div>
    </React.Fragment>
  );
};


export type Item = {
  id: string;
  primary: string;
  secondary: number;
  index: number;
  min?: number,
  max?: number,
  step?: number
};


export type DraggableListProps = {
  items: Item[];
  onDragEnd: OnDragEndResponder;
  setData: any;
  inputName: string;
};

export const DraggableListItem = (props) => {
  const {item, index, setData, inputName, listLength} = props;
  const classes = useStyles();
  const useStaticListItem = (index === listLength-1) ? '' : classes.staticListItem;

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <ListItem
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={snapshot.isDragging ? classes.draggingListItem : useStaticListItem}
        >
          <div className={classes.flexGrow}>
            <Grid container spacing={0}>
              <Grid item xs>
                <Typography>Category Name</Typography>
              </Grid>
              <Grid item xs>
                <Typography>{inputName}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={0}>
              <Grid item xs>
                <InputLabel>{item.primary}</InputLabel>
              </Grid>
              <Grid item xs>
                <NumberField
                  data={{
                    value: item.secondary,
                    error: item.error
                  }}
                  setData={setData}
                  min={item.min}
                  max={item.max}
                  step={item.step}
                  index={item.index}
                />
              </Grid>
            </Grid>
          </div>
        </ListItem>
      )}
    </Draggable>
  );
};



export const DraggableList = React.memo(({ items, onDragEnd, setData, inputName }: DraggableListProps) => {

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable-list">
        {provided => (
          <div ref={provided.innerRef}  {...provided.droppableProps}>
            {items.map((item, index) => (
              <DraggableListItem
                item={item}
                index={index}
                setData={setData}
                inputName={inputName}
                key={item.id}
                listLength={items.length}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
});
